import { useState, useEffect, useCallback } from "react";
import CustomSelectBox from "../../components/Inputs/CustomSelectBox";
import TextAreaInput from "../../components/Inputs/TextAreaInput";
import CustomButton from "../../components/CustomButton";
import { TfiReload } from "react-icons/tfi";
import styles from "./ConferenceRecommender.module.css";
import { TbExternalLink } from "react-icons/tb";
import { FaRegThumbsUp, FaRegThumbsDown } from "react-icons/fa";
import ReminderModal from "../../components/modals/ReminderModal";
import { FaRegCalendarAlt } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import { TbHomeFilled } from "react-icons/tb";
import { TbCalendarStats } from "react-icons/tb";
import { BiSolidBellRing } from "react-icons/bi";
import { HiMiniBellSnooze } from "react-icons/hi2";
import { MdOutlineFilterAltOff } from "react-icons/md";
import {
  generateAbstractWithAI,
  getRecommendedConferences,
  voteToConferences, // <-- add this import
  getReminders,
  createReminder,
  deleteReminder,
} from "../../api/Api";
import { useProject } from "../../contexts/ProjectContextProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import Pagination from "../../components/ui/Pagination";
import VoteBox from '../../components/ui/vote/VoteBox';
import { showToast } from "../../utils/toastUtils";
import { useUser } from "../../contexts/UserContextProvider";
import { useNavigate } from "react-router-dom";

// Conference Card Component
const ConferenceCard = ({ uniqueId, conference, onRate, onSetReminder, onRemoveReminder, reminder,onFeedbackSubmit,remindersLoading }) => {
  const getMatchBadgeStyles = (match) => {
    if (match >= 90) {
      return { backgroundColor: "#63D06E" };
    }
    if (match >= 70) {
      return { backgroundColor: "#ffc107" };
    }
    return { backgroundColor: "#adb0b4" };
  };

  const getMatchBorderSyles = (match) => {
    if (match >= 90) {
      return { border: "1px solid #63D06E", borderRadius: "10px" };
    }
    if (match >= 70) {
      return { border: "1px solid #ffc107", borderRadius: "10px" };
    }
    return {};
  };

  return (
    <div
      className="card mb-3 position-relative"
      style={{ borderRadius: "10px" }}
    >
      <div
        className="card-body p-4 "
        style={getMatchBorderSyles(conference.match)}
      >
        <div className="d-flex justify-content-between ">
          <h5 className="p3 fw-semibold mb-1">{conference.title}</h5>
          <div>
            <span
              className={`d-flex badge  ${styles.rounded}`}
              style={getMatchBadgeStyles(conference.match)}
            >
              {conference.match}% Match
            </span>
          </div>
        </div>
        <h6 className="mb-2 text-muted p5">{conference.organization}</h6>
        <p className="p4 text-secondary">{conference.description}</p>

        <div className="mb-3">
          <div className="row mb-2">
            <div className="col-12 col-md-4 d-flex align-items-center mb-2 mb-md-0 p-0">
              <FaRegCalendarAlt className="me-2" />
              <span className="p4 fw-semibold pe-2">Date: </span> {conference.date}
            </div>
            <div className="col-10 col-md-4 d-flex align-items-center mb-2 mb-md-0 p-0">
              <TbCalendarStats className="me-2" />
              <span className="p4 fw-semibold pe-2">Submission Deadline: </span>
              {conference.submissionDeadline}
            </div>
          </div>
          <div className="row">
            <div className="col-10 col-md-4 d-flex align-items-center mb-2 mb-md-0 p-0">
              <GrLocation className="me-2" />
              <span className="p4 fw-semibold pe-2">Location: </span>
              {conference.location}
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
          <div className="btn-group gap-2 flex-wrap">
            <CustomButton
              variant="outline"
              className="btn-sm  px-3 py-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() =>
                conference.websiteUrl &&
                window.open(conference.websiteUrl, "_blank")
              }
              disabled={!conference.websiteUrl}
            >
              <TbExternalLink size={18} />{" "}
              <span className="fw-normal">Visit Conference Website</span>
            </CustomButton>
            <CustomButton
              variant="outline"
              className="btn-sm  px-3 py-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => reminder ? onRemoveReminder(conference.id) : onSetReminder(conference.id)}
              disabled={remindersLoading}
            >
              {reminder ? (
                <HiMiniBellSnooze size={20} />
              ) : (
                <BiSolidBellRing size={18} />
              )}
              <span className="fw-normal">
                {reminder ? "Disable Reminder" : "Enable Reminder"}
              </span>
            </CustomButton>

            {reminder && reminder.type === 'preset' && (
              <span className="ms-2 text-secondary small">{reminder.value}</span>
            )}
          </div>
          <VoteBox
            itemId={conference.id} 
            feedbackType={conference.feedback} // Optionally track last vote for this conference
            onRate={(voteType, itemId, feedbackMessage) => onRate(itemId, voteType, feedbackMessage)}
            additionalData={[conference.websiteUrl]}
            itemType="conference"
            onSuccess={onFeedbackSubmit}
          />
        </div>
      </div>
    </div>
  );
};



// Helper to get composite key for reminders and conferences
const getReminderKey = (title, start_date) => `${title}|${start_date}`;

// Helper to normalize title and date
function normalizeTitle(title) {
  return title ? title.trim().toLowerCase() : '';
}
function normalizeDate(dateStr) {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

const ConferenceRecommender = () => {
  const navigate = useNavigate();
  // State to store all conferences data from API
  const [allConferences, setAllConferences] = useState([]);
  const [isLoadingConferences, setIsLoadingConferences] = useState(false);
  const [conferenceError, setConferenceError] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  
  // Function to fetch conferences from API
  const fetchConferences = async (abstract) => {
    try {
      setIsLoadingConferences(true);
      setConferenceError(null);

      // Set the aiPrompt to the current abstract
      setAiPrompt(abstract);

      const response = await getRecommendedConferences({ abstract });

      if (response?.status === 200) {
        // Transform API response to match component structure
        const transformedConferences = response.data.conferences.map(
          (conf) => ({
            ...conf,
            match: Math.round(conf.match_score * 100), // Convert to percentage
          })
        );
        setAllConferences(transformedConferences);
        // Reset pagination when new data is loaded
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      } else {
        throw new Error("Failed to fetch conferences");
      }
    } catch (error) {
      console.error("Error fetching conferences:", error);
      setConferenceError(error.message || "Failed to fetch conferences");
    } finally {
      setIsLoadingConferences(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      projectId: "",
      workStreamId: "",
      isConnectChecked: true,
      abstract: "",
    },
    validationSchema: Yup.object({
      projectId: Yup.string().when("isConnectChecked", {
        is: (val) => val === true,
        then: () => Yup.string().required("Project is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      workStreamId: Yup.string().when("isConnectChecked", {
        is: (val) => val === true,
        then: () => Yup.string().required("Work Stream is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      abstract: Yup.string().notRequired(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setIsLoading(true);
        setApiError(null);

        if (values.isConnectChecked) {
          const response = await generateAbstractWithAI({
            projectId: values.projectId,
            workStreamId: values.workStreamId,
          });

          if (response?.status === 200) {
            formik.setFieldValue("abstract", response.data?.abstract);
            setAiPrompt(response.data?.abstract);
          } else {
            throw new Error("Failed to generate abstract");
          }
        } else {
          // If not using AI generation, set the aiPrompt to the manually entered abstract
          setAiPrompt(values.abstract);
        }
      } catch (error) {
        console.error("Error:", error);
        setApiError(
          error.message || "An error occurred while processing your request"
        );
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Replace the separate isConnectChecked state with formik value
  const handleConnectToggle = () => {
    formik.setFieldValue("isConnectChecked", !formik.values.isConnectChecked);
    if (!formik.values.isConnectChecked) {
      formik.setFieldValue("projectId", "");
      formik.setFieldValue("workStreamId", "");
    }
  };

  // State to store displayed conferences after filtering/sorting
  const [conferences, setConferences] = useState(allConferences);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });

  // Calculate paginated conferences from filtered results
  const paginatedConferences = conferences.slice(
    (pagination.currentPage - 1) * pagination.itemsPerPage,
    pagination.currentPage * pagination.itemsPerPage
  );

  const { userInfo } = useUser();
  const [reminders, setReminders] = useState([]); // Array of reminders from backend
  const [reminderMap, setReminderMap] = useState({}); // Map for ConferenceCard
  const [remindersLoading, setRemindersLoading] = useState(false);

  // Fetch reminders from backend
  useEffect(() => {
    const loadReminders = async () => {
      if (!userInfo?.id) return;
      setRemindersLoading(true);
      try {
        const response = await getReminders(userInfo.id);
        if (response?.data) {
          setReminders(response.data);
          // Build map: { [title|start_date]: reminder }
          const map = {};
          response.data.forEach(r => {
            if (r.conference_title && r.conference_start_date) {
              const key = getReminderKey(normalizeTitle(r.conference_title), normalizeDate(r.conference_start_date));
              console.log('Building map - title:', normalizeTitle(r.conference_title), 'start_date:', normalizeDate(r.conference_start_date), 'key:', key);
              map[key] = r;
            }
          });
          setReminderMap(map);
          console.log('All reminder keys:', Object.keys(map));
        }
      } catch (e) {
        console.error("Error fetching reminders:", e);
      } finally {
        setRemindersLoading(false);
      }
    };
    loadReminders();
  }, [userInfo]);

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedConferenceId, setSelectedConferenceId] = useState(null);
  const {
    data: projects,
    error,
    loading,
    getWorkStreams,
    workStreams,
  } = useProject();
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Filter and sort states
  const [virtualOnly, setVirtualOnly] = useState(false);
  const [subjectArea, setSubjectArea] = useState("");
  const [sortBy, setSortBy] = useState("");

  // Generate subject options dynamically from API data
  const subjectOptions = [
    { value: "", label: "All Subjects" },
    ...Array.from(new Set(allConferences.map((conf) => conf.subjectArea)))
      .filter(Boolean)
      .map((area) => ({ value: area, label: area })),
  ];

  const sortOptions = [
    { value: "match", label: "Match Percentage" },
    { value: "date", label: "Conference Date" },
  ];

  // Apply filters and sorting whenever filter criteria change
  useEffect(() => {
    // Start with all conferences
    let filteredConferences = [...allConferences];

    // Apply Virtual Only filter if enabled
    if (virtualOnly) {
      filteredConferences = filteredConferences.filter(
        (conference) => conference.isVirtual
      );
    }

    // Apply Subject Area filter if selected
    if (subjectArea) {
      filteredConferences = filteredConferences.filter(
        (conference) => conference.subjectArea === subjectArea
      );
    }

    // Apply sorting if selected
    if (sortBy === "match") {
      filteredConferences.sort((a, b) => b.match - a.match);
    } else if (sortBy === "date") {
      filteredConferences.sort((a, b) => {
        // Better date parsing - handle different date formats
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB; // Ascending order (earliest first)
      });
    }
    // If sortBy is empty, keep original order

    // Update the displayed conferences
    setConferences(filteredConferences);

    // Reset pagination to first page when filters change
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [allConferences, virtualOnly, subjectArea, sortBy]);


  const handleRate = useCallback(async (conferenceId, voteType, feedbackMessage) => {
    console.log("ratingType", voteType);
    console.log("conferenceId", conferenceId);
    
    // Find the conference object to get the siteurl
    const conference = allConferences.find((c) => c.id === conferenceId);
    if (!conference) return;
    
    setConferences(pre => pre.map((c) => c.id === conferenceId ? { ...c, feedback: voteType } : c));
    const vote = voteType === "positive" ? true : voteType === "negative" ? false : null;
    const response = await voteToConferences({
      siteurl: conference.websiteUrl,
      vote: vote,
      feedback: feedbackMessage,
      aiPrompt: aiPrompt || formik.values.abstract,
    });
    if (response?.status === 200) {
      if(vote) showToast("Thank you for your feedback!", "success");
    } else {
      showToast("Failed to submit vote", "error");
    }

  }, [allConferences, aiPrompt, formik.values.abstract]);

  const handleSetReminder = (conferenceId) => {
    setSelectedConferenceId(conferenceId);
    setShowReminderModal(true);
  };

  // Accept the reminder object from ReminderModal
  const handleSaveReminder = async (reminderObj) => {
    const conference = allConferences.find(c => c.id === selectedConferenceId);
    if (!conference || !userInfo?.id) return;

    let valueToSend;
    // If reminderObj.value is a date string, calculate days before deadline
    if (reminderObj.value && reminderObj.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const customDate = new Date(reminderObj.value);
      const deadlineDate = new Date(conference.submissionDeadline);
      if (customDate > deadlineDate) {
        showToast("Reminder date cannot be after the submission deadline", "error");
        setShowReminderModal(false);
        return;
      }
      valueToSend = daysBetween(customDate, deadlineDate).toString();
    } else {
      // Otherwise, treat as preset value (number of days as string)
      valueToSend = reminderObj.value || reminderObj.type;
    }

    let payload = {
      user_id: userInfo.id,
      conference: {
        title: conference.title,
        start_date: conference.start_date || conference.date,
        submission_deadline: conference.submissionDeadline,
        organization: conference.organization, // Use organization name instead of ID
        description: conference.description,
        end_date: conference.end_date,
        location: conference.location,
        venue: conference.venue,
        website_url: conference.websiteUrl,
        is_virtual: conference.isVirtual,
        subject_area: conference.subjectArea,
        match_score: conference.match,
      },
      value: valueToSend // Send only the calculated days value
    };

    try {
      await createReminder(payload);
      // Refresh reminders
      const response = await getReminders(userInfo.id);
      if (response?.data) {
        setReminders(response.data);
        const map = {};
        response.data.forEach(r => {
          if (r.conference_title && r.conference_start_date) {
            const key = getReminderKey(normalizeTitle(r.conference_title), normalizeDate(r.conference_start_date));
            map[key] = r;
          }
        });
        setReminderMap(map);
        showToast("Reminder enabled successfully", "success");
      }
    } catch (e) {
      console.error("Error saving reminder:", e);
    }
    setShowReminderModal(false);
  };

  // Remove a reminder via backend
  const handleRemoveReminder = async (conferenceId) => {
    const conference = allConferences.find(c => c.id === conferenceId);
    if (!conference) return;
    const key = getReminderKey(normalizeTitle(conference.title), normalizeDate(conference.start_date || conference.date));
    const reminder = reminderMap[key];
    if (!reminder) return;
    try {
      await deleteReminder(reminder.id);
      // Refresh reminders
      const response = await getReminders(userInfo.id);
      if (response?.data) {
        setReminders(response.data);        
        const map = {};
        response.data.forEach(r => {
          if (r.conference_title && r.conference_start_date) {
            const key = getReminderKey(normalizeTitle(r.conference_title), normalizeDate(r.conference_start_date));
            map[key] = r;
          }
        });
        setReminderMap(map);
        showToast("Reminder disabaled successfully", "success");
      }
    } catch (e) {
      console.error("Error removing reminder:", e);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setVirtualOnly(false);
    setSubjectArea("");
    setSortBy("");
  };

  return (
    <>
      <div
        className="container-fluid p-4"
        style={{ backgroundColor: "#F9FAFC" }}
      >

        <form onSubmit={formik.handleSubmit} className="container">
          
          <header className="mb-4 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="fw-bold">Conference Recommender</h4>
              <p className="text-muted p4 m-0">
                Find the perfect conferences for your research paper based on your
                abstract.
              </p>
            </div>
            {/* Reminders Button in top-right */}
            <CustomButton
              onClick={() => navigate("/reminders")}
            >
              Reminders
            </CustomButton>
          </header>

          <div className="container bg-white p-4 rounded">
            <div className="mb-4 d-flex align-items-start gap-2">
              <input
                type="checkbox"
                id="connect"
                className={`form-check-input mt-1 me-2 ${styles.roundCheckbox}`}
                checked={formik.values.isConnectChecked}
                onChange={handleConnectToggle}
              />
              <label htmlFor="connect" className="form-check-label text-sm">
                Connect an existing project to generate an abstract with AI
              </label>
            </div>
            {formik.values.isConnectChecked && (
              <>
                <div className="mb-2">
                  <CustomSelectBox
                    label="Select Project"
                    id="projectId"
                    options={projects?.map((project) => ({
                      value: project.id,
                      label: project.name,
                    }))}
                    value={formik.values.projectId}
                    onChange={async (e) => {
                      formik.setFieldValue("workStreamId", "");
                      formik.setFieldValue("projectId", e.target.value);
                      await getWorkStreams(e.target.value);
                    }}
                    fullWidth
                    labelColor="#000"
                    className="w-100"
                    style={{ marginBottom: "8px" }}
                    error={formik.touched.projectId && formik.errors.projectId}
                  />
                </div>

                <div className="mb-2">
                  <CustomSelectBox
                    label="WorkStream"
                    id="workStreamId"
                    options={workStreams?.data?.map((workStream) => ({
                      value: workStream.id,
                      label: workStream.name,
                    }))}
                    placeholder={
                      !formik.values.projectId
                        ? "Select an Option"
                        : workStreams?.data?.length > 0
                        ? "Select WorkStream"
                        : "No WorkStream Found for this project"
                    }
                    value={formik.values.workStreamId}
                    onChange={formik.handleChange}
                    fullWidth
                    labelColor="#000"
                    className="w-100"
                    style={{ marginBottom: "8px" }}
                    error={
                      formik.touched.workStreamId && formik.errors.workStreamId
                    }
                  />
                </div>
              </>
            )}

            <div className="d-flex flex-column flex-sm-row gap-3 mb-3">
              <CustomButton
                className="btn-sm px-5 py-2 d-flex items-center justify-center gap-2 flex-1"
                type="submit"
                disabled={
                  isLoading ||
                  (formik.values.isConnectChecked &&
                    (!formik.values.projectId || !formik.values.workStreamId))
                }
              >
                {formik.values.abstract ? (
                  <span className="d-flex align-items-center gap-2">
                    <TfiReload />
                    <span>
                      {isLoading && formik.values.abstract
                        ? "Generating..."
                        : "Regenerate"}
                    </span>
                  </span>
                ) : (
                  <span>{isLoading ? "Processing..." : "Generate"}</span>
                )}
              </CustomButton>
            </div>
          </div>

          <div className="mb-1 mt-4">
            <p className="mb-2 p3">Generate or Paste your full abstract here</p>
            <TextAreaInput
              id="abstract"
              name="abstract"
              value={formik.values.abstract}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Paste your abstract here..."
              rows={4}
              className="w-100 border border-gray-300 rounded p-2"
              style={{ resize: "vertical" }}
              error={formik.touched.abstract && formik.errors.abstract}
            />
          </div>

          <p className="text-xs text-gray-500 mb-3">
            For the best results, include your complete abstract with key
            findings & methodology.
          </p>

          <CustomButton
            className="btn-sm py-2 d-flex align-items-center justify-content-center gap-2"
            onClick={() => fetchConferences(formik.values.abstract)}
            disabled={!formik.values.abstract || isLoadingConferences}
            style={{ width: "fit-content" }}
          >
            <span>
              {isLoadingConferences
                ? "Finding conferences..."
                : "Find matching conferences"}
            </span>
          </CustomButton>

          {conferenceError && (
            <div className="alert alert-danger mt-3" role="alert">
              {conferenceError}
            </div>
          )}

          {apiError && (
            <div className="alert alert-danger mt-3" role="alert">
              {apiError}
            </div>
          )}
        </form>
      </div>

      {allConferences.length > 0 && <div className="container mt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start align-items-lg-center mb-4">
          <h2 className="h5 m-0">
            Recommended Conferences ({conferences.length})
          </h2>

          <div className="d-flex flex-column flex-sm-row gap-3 mt-3 mt-lg-0">
            {/* Virtual Only Toggle Switch */}
            <div className="d-flex align-items-center gap-2">
              <span>Virtual Only</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="virtualOnlySwitch"
                  checked={virtualOnly}
                  onChange={() => setVirtualOnly(!virtualOnly)}
                  style={{ width: "40px", height: "20px" }}
                />
              </div>
            </div>

            {/* Subject Area */}
            <div style={{width: "350px"}} className="d-flex flex-column flex-sm-row gap-3 mt-3 mt-lg-0 align-items-center">
              <span className="text-nowrap">Subject Area</span>

              <CustomSelectBox
                id="subject-filter"
                options={subjectOptions}
                value={subjectArea}
                onChange={(e) => setSubjectArea(e.target.value)}
                fullWidth
                hideLabel={true}
                className="mt-3 p-2"
              />
            </div>

            {/* Sort By  */}
            <div className="d-flex flex-column flex-sm-row gap-3  mt-lg-0 align-items-center">
              <span>Sort By</span>
              <CustomSelectBox
                id="sort-filter"
                options={sortOptions}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                fullWidth={false}
                hideLabel={true}
                className="mt-3 p-2"
              />
            </div>

            {/* Filter Reset Button */}
            <div>
              <button
                className="btn btn-light rounded-pill d-flex align-items-center p-2  mt-3"
                onClick={resetFilters}
                title="Reset Filters"
              >
                <MdOutlineFilterAltOff size={21} />
              </button>
            </div>
          </div>
        </div>
        <div>
          {isLoadingConferences ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading conferences...</span>
              </div>
              <span className="ms-3">Finding matching conferences...</span>
            </div>
          ) : conferences.length > 0 ? (
            <>
              {paginatedConferences.map((conference) => (
                <div
                  className="col-sm-12 col-md-12 col-lg-10 col-xl-12"
                  key={conference.id}
                >
                  <ConferenceCard
                    uniqueId={conference.websiteUrl}
                    conference={conference}
                    onRate={handleRate}
                    onSetReminder={handleSetReminder}
                    onRemoveReminder={handleRemoveReminder}
                    reminder={(() => {
                      const reminderKey = getReminderKey(normalizeTitle(conference.title), normalizeDate(conference.start_date || conference.date));
                      const reminder = reminderMap[reminderKey];
                      return reminder;
                    })()}
                    remindersLoading={remindersLoading}
                  />
                </div>
              ))}
              <Pagination
                totalItems={conferences.length}
                pageSize={pagination.itemsPerPage}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, currentPage: page }))
                }
                currentPage={pagination.currentPage}
              />
            </>
          ) : allConferences.length === 0 ? null : (
            <div className="alert alert-warning">
              <h6>No conferences match your filters</h6>
              <p className="mb-0">
                Try adjusting your filter criteria or reset filters to see all
                conferences.
              </p>
            </div>
          )}
        </div>
      </div>}

      {/* Reminder Modal */}
      {(() => {
        const selectedConference = allConferences.find(c => c.id === selectedConferenceId);
        const selectedReminderKey = selectedConference ? getReminderKey(normalizeTitle(selectedConference.title), normalizeDate(selectedConference.start_date || selectedConference.date)) : null;
        const selectedReminder = selectedReminderKey ? reminderMap[selectedReminderKey] : null;
        return (
          <ReminderModal
            show={showReminderModal}
            onHide={() => setShowReminderModal(false)}
            onSave={handleSaveReminder}
            defaultDateTime={
              selectedReminder?.type === 'custom'
                ? selectedReminder?.value
                : ''
            }
          />
        );
      })()}
    </>
  );
};

// Helper to calculate days between two dates (ignoring time)
function daysBetween(date1, date2) {
  const d1 = new Date(Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate()));
  const d2 = new Date(Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate()));
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
}

export default ConferenceRecommender;
